import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function CustomerGroups() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", tag: "", status: "Active" });

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get("/groups");
    setRows(r.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    if (!form.name || !form.tag) return toast.error("Name and tag are required");
    try {
      await api.post("/groups", form);
      toast.success("Group created");
      setOpen(false);
      setForm({ name: "", tag: "", status: "Active" });
      load();
    } catch {
      toast.error("Failed to create group");
    }
  }

  async function toggleStatus(g) {
    const status = g.status === "Active" ? "Inactive" : "Active";
    await api.patch(`/groups/${g.id}`, { status });
    load();
  }

  async function remove(id) {
    if (!window.confirm("Delete this group?")) return;
    await api.delete(`/groups/${id}`);
    toast.success("Group deleted");
    load();
  }

  return (
    <div data-testid="groups-page">
      <PageHeader
        title="Customer Groups"
        subtitle="Segment your wholesale buyers using tags"
        actions={
          <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="group-create-btn">
            <Plus size={14} /> Create customer group
          </button>
        }
      />

      <div className="b2b-card">
        {loading ? (
          <div className="p-8 text-sm text-center" style={{ color: "var(--brand-muted)" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No customer groups" subtitle="Create your first group to segment wholesalers." testId="groups-empty" />
        ) : (
          <table className="b2b-table" data-testid="groups-table">
            <thead>
              <tr>
                <th>Customer group</th>
                <th>Members</th>
                <th>Tag</th>
                <th>Status</th>
                <th>Created at</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.id} data-testid={`group-row-${g.id}`}>
                  <td className="font-medium">{g.name}</td>
                  <td>{g.members ?? 0}</td>
                  <td><span className="badge" style={{ background: "#e6ecf7", color: "#1a2340" }}>{g.tag}</span></td>
                  <td>
                    <label className="switch">
                      <input type="checkbox" checked={g.status === "Active"} onChange={() => toggleStatus(g)} data-testid={`group-toggle-${g.id}`} />
                      <span className="slider" />
                    </label>
                    <span className="ml-2 text-xs" style={{ color: "var(--brand-muted)" }}>{g.status}</span>
                  </td>
                  <td className="text-sm" style={{ color: "var(--brand-muted)" }}>{new Date(g.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="btn-danger" onClick={() => remove(g.id)} data-testid={`group-delete-${g.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Create customer group"
        testId="group-create-modal"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setOpen(false)} data-testid="group-create-cancel">Cancel</button>
            <button className="btn-primary" onClick={create} data-testid="group-create-save">Create group</button>
          </>
        }
      >
        <form onSubmit={create} className="space-y-4">
          <div>
            <label className="form-label">Group name</label>
            <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-form-name" />
          </div>
          <div>
            <label className="form-label">Customer tag</label>
            <input className="form-input" placeholder="e.g. Silver, Bronze, Wholesaler" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} data-testid="group-form-tag" />
          </div>
          <div>
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="group-form-status">
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}
